import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener('click', function (event) {
      if (event.target) {
        const target = event.target.closest('[data-action="collapse"]');

        if (target) {
          const content = target.nextElementSibling;

          target.classList.toggle('active');
          
          if (content.style.maxHeight) {
            content.style.maxHeight = null;
          } else {
            content.style.maxHeight = content.scrollHeight + 'px';
          }
        }
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const remove = () => cartItemRowToRemove.remove();

    if (prefersReducedMotion()) return remove();

    // Add class to the row to trigger the animation
    cartItemRowToRemove.style.setProperty('--row-height', `${cartItemRowToRemove.clientHeight}px`);
    cartItemRowToRemove.classList.add('removing');

    // Remove the row after the animation ends
    onAnimationEnd(cartItemRowToRemove, remove);
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
        
        // Extract cart total from response if available
        const cartTotal = this.#getCartTotalFromResponse(parsedResponseText);
        // Update Tea Society promotion components after morphSection completes
        this.#updateTeaSocietyPromotions(cartTotal);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    this.#beforeUpdateTeaSocietyPromotion();
    if (event instanceof DiscountUpdateEvent) {
      // Extract cart total from DiscountUpdateEvent resource (cart update API response)
      const cartTotal = this.#getCartTotalFromEvent(event);
      sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => {
        // Update Tea Society promotion components after section render completes
        this.#updateTeaSocietyPromotions(cartTotal);
      });
      return;
    }
    if (event.target === this) return;

    // Extract cart total from event data if available
    const cartTotal = this.#getCartTotalFromEvent(event);
    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      // Update Tea Society promotion components after morphSection completes
      this.#updateTeaSocietyPromotions(cartTotal);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => {
        // Update Tea Society promotion components after section render completes
        this.#updateTeaSocietyPromotions(cartTotal);
      });
    }
  };

  /**
   * Extracts cart total from event data.
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   * @returns {number | null}
   */
  #getCartTotalFromEvent(event) {
    // For DiscountUpdateEvent, resource is the cart update API response
    if (event instanceof DiscountUpdateEvent) {
      const resource = /** @type {{ total_price?: number, original_total_price?: number } | undefined} */ (event.detail?.resource);
      if (resource) {
        // Use total_price (after discounts) for accurate savings calculation
        if (typeof resource.total_price === 'number') {
          return resource.total_price;
        }
        if (typeof resource.original_total_price === 'number') {
          return resource.original_total_price;
        }
      }
    }

    // For CartUpdateEvent/CartAddEvent, check resource and data
    const resource = /** @type {{ total_price?: number, original_total_price?: number } | undefined} */ (event.detail?.resource);
    if (resource) {
      if (typeof resource.total_price === 'number') {
        return resource.total_price;
      }
      if (typeof resource.original_total_price === 'number') {
        return resource.original_total_price;
      }
    }

    return null;
  }

  /**
   * Extracts cart total from cart update API response.
   * @param {{ total_price?: number, original_total_price?: number }} response - The cart update API response
   * @returns {number | null}
   */
  #getCartTotalFromResponse(response) {
    // Check if response has cart data directly
    if (response && typeof response.total_price === 'number') {
      return response.total_price;
    }
    if (response && typeof response.original_total_price === 'number') {
      return response.original_total_price;
    }

    return null;
  }

  /**
   * Updates all Tea Society promotion components with the latest cart total.
   * Uses cart data from event if available, otherwise fetches from /cart.js.
   * @param {number | null} [cartTotalFromEvent] - Cart total from event data (optional)
   */
  async #updateTeaSocietyPromotions(cartTotalFromEvent = null) {
    const components = document.querySelectorAll('tea-society-promotion-component');

    let cartTotal = cartTotalFromEvent;

    // If we don't have cart total from event, fetch it
    if (typeof cartTotal !== 'number') {
      try {
        const response = await fetch('/cart.js', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          // Remove calculating class on error
          for (const component of components) {
            if (component instanceof HTMLElement) {
              component.classList.remove('calculating');
            }
          }
          return;
        }
        const cart = await response.json();
        
        // Use total_price (after discounts) for accurate savings calculation
        cartTotal = typeof cart?.total_price === 'number' 
          ? cart.total_price 
          : cart?.original_total_price;
      } catch (_) {
        // Remove calculating class on error
        for (const component of components) {
          if (component instanceof HTMLElement) {
            component.classList.remove('calculating');
          }
        }
        return;
      }
    }

    if (typeof cartTotal !== 'number') {
      // Remove calculating class if no valid cart total
      for (const component of components) {
        if (component instanceof HTMLElement) {
          component.classList.remove('calculating');
        }
      }
      return;
    }

    // Update all components (calculating class will be removed by #calculateAndUpdateSavings)
    for (const component of components) {
      // Type check and call updateCartTotal method
      if (component instanceof HTMLElement && 'updateCartTotal' in component) {
        /** @type {any} */ (component).updateCartTotal(cartTotal);
      }
    }
  }

  #beforeUpdateTeaSocietyPromotion() {
    const components = document.querySelectorAll('tea-society-promotion-component');
    for (const component of components) {
      if (component instanceof HTMLElement) {
        component.classList.add('calculating');
      }
    }
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
